import * as THREE from "three";
import { ResourceID } from "../../../../resource_loader/resource_id.js";
import { InstanceBatch } from "../../../core/batches/instance_batch.js";
import { Vfx3DGlobalAtlas } from "../../../core/textures/vfx_3d_global_atlas.js";

import { AttributeSize } from "../../../core/types/core_types.js";

export const FireworkExplosionBlastLayout = {
  aPosStartTime: AttributeSize.Vec4, // xyz: start_pos, w: start_time
  aScaleRotDur: AttributeSize.Vec4, // x: start_scale, y: end_scale, z: initial_angle, w: duration
  aRotationAxisSpeed: AttributeSize.Vec4, // xyz: rotation_axis, w: rotation_speed
  aDissolveParams: AttributeSize.Vec3, // x: dissolve_start_value, y: dissolve_end_value, z: dissolve_start_progress
} as const;

export class VfxFireworkExplosionBlastBatch extends InstanceBatch<typeof FireworkExplosionBlastLayout>
{
  private static readonly MAX_INSTANCES: number = 6;
  private static readonly RENDER_ORDER: number  = 21;

  // --- Visuals & Color Palette (два кольори 0xFFDC50 і 0xFF8C3C) ---
  private static readonly COLOR_1: THREE.Color = new THREE.Color( 0xFFDC50 );  // Початковий колір
  private static readonly COLOR_2: THREE.Color = new THREE.Color( 0xFF8C3C );  // Фінальний колір

  // --- Animation & Dissolve Parameters ---
  private static readonly DISSOLVE_SOFTNESS: number = 0.2;  // М'якість та плавність прозорості за шумом
  private static readonly FIRE_END_RATIO: number    = 0.6;  // Повний перехід до другого кольору

  private static cached_geometry: THREE.BufferGeometry | null = null;

  public constructor()
  {
    const base_geometry = VfxFireworkExplosionBlastBatch.getSphereGeometry();

    let atlas_texture: THREE.Texture | null = null;
    const noise_uv = new THREE.Vector4( 0, 0, 1, 1 );
    try
    {
      atlas_texture = Vfx3DGlobalAtlas.getTexture();
      const uv = Vfx3DGlobalAtlas.getUVRect( ResourceID.VornoiNoiseTexture );
      noise_uv.set( uv.min_x, uv.min_y, uv.max_x, uv.max_y );
    }
    catch ( _e )
    {
      // Atlas will be populated lazily in spawnSphere if called before init
    }

    const material = new THREE.ShaderMaterial( {
      uniforms: {
        uNoiseTexture:     { value: atlas_texture },
        uNoiseUv:          { value: noise_uv },
        uColor1:           { value: VfxFireworkExplosionBlastBatch.COLOR_1 },
        uColor2:           { value: VfxFireworkExplosionBlastBatch.COLOR_2 },
        uDissolveSoftness: { value: VfxFireworkExplosionBlastBatch.DISSOLVE_SOFTNESS },
        uFireEndRatio:     { value: VfxFireworkExplosionBlastBatch.FIRE_END_RATIO },
        uTime:             { value: 0.0 },
      },
      vertexShader: `
        uniform float uTime;

        attribute vec4 aPosStartTime;
        attribute vec4 aScaleRotDur;
        attribute vec4 aRotationAxisSpeed;
        attribute vec3 aDissolveParams;

        varying vec3 vLocalPos;
        varying vec3 vBlend;
        varying vec3 vViewNormal;
        varying float vProgress;
        varying vec3 vDissolveParams;

        vec3 rotateAroundAxis( vec3 p, vec3 axis, float angle ) {
          float c = cos( angle );
          float s = sin( angle );
          return p * c + cross( axis, p ) * s + axis * dot( axis, p ) * ( 1.0 - c );
        }

        void main() {
          float start_time = aPosStartTime.w;
          float duration = aScaleRotDur.w;
          float age = max( 0.0, uTime - start_time );
          float progress = clamp( age / duration, 0.0, 1.0 );
          vProgress = progress;
          vDissolveParams = aDissolveParams;

          // Плавне розширення сфери (Ease-out): швидкий старт вибуху з поступовим уповільненням
          float scale_progress = 1.0 - pow( 1.0 - progress, 2.2 );
          float current_scale = mix( aScaleRotDur.x, aScaleRotDur.y, scale_progress );

          // 3D-обертання текстурних координат на поверхні сфери
          float angle = aScaleRotDur.z + aRotationAxisSpeed.w * age;
          vec3 rot_axis = length( aRotationAxisSpeed.xyz ) > 0.001 ? normalize( aRotationAxisSpeed.xyz ) : vec3( 0.0, 1.0, 0.0 );
          vec3 rotated_pos = rotateAroundAxis( position, rot_axis, angle );
          vec3 rotated_normal = rotateAroundAxis( normal, rot_axis, angle );

          vLocalPos = rotated_pos;
          
          // Швидкий розрахунок ваг Triplanar (n^4 без pow/log2/exp2) на вершинах замість пікселів
          vec3 n = abs( normalize( rotated_normal ) );
          vec3 blend = n * n;
          blend *= blend;
          vBlend = blend / ( blend.x + blend.y + blend.z );

          vViewNormal = normalize( ( modelViewMatrix * vec4( normal, 0.0 ) ).xyz );

          vec4 mv_position = modelViewMatrix * vec4( aPosStartTime.xyz, 1.0 );
          mv_position.xyz += position * current_scale;
          gl_Position = projectionMatrix * mv_position;
        }
      `,
      fragmentShader: `
        uniform sampler2D uNoiseTexture;
        uniform vec4 uNoiseUv;
        uniform vec3 uColor1;
        uniform vec3 uColor2;
        uniform float uDissolveSoftness;
        uniform float uFireEndRatio;

        varying vec3 vLocalPos;
        varying vec3 vBlend;
        varying vec3 vViewNormal;
        varying float vProgress;
        varying vec3 vDissolveParams;

        void main() {
          vec3 norm_pos = clamp( vLocalPos * 0.5 + 0.5, 0.0, 1.0 );

          vec2 uv_x = mix( uNoiseUv.xy, uNoiseUv.zw, norm_pos.zy );
          vec2 uv_y = mix( uNoiseUv.xy, uNoiseUv.zw, norm_pos.xz );
          vec2 uv_z = mix( uNoiseUv.xy, uNoiseUv.zw, norm_pos.xy );

          float sample_x = texture2D( uNoiseTexture, uv_x ).r;
          float sample_y = texture2D( uNoiseTexture, uv_y ).r;
          float sample_z = texture2D( uNoiseTexture, uv_z ).r;

          float noise = sample_x * vBlend.x + sample_y * vBlend.y + sample_z * vBlend.z;

          // 1. ПЛАВНЕ РОЗЧИНЕННЯ ЗА ШУМОМ
          // vDissolveParams.x = start_dissolve_value (0.0 = ціла сфера, 0.5 = напіврозчинена, 1.0 = повністю розчинена)
          // vDissolveParams.y = end_dissolve_value (1.0 = повністю розчинена)
          // vDissolveParams.z = dissolve_start_progress (0.0..1.0 час початку подальшого розчинення)
          float start_threshold = mix( -uDissolveSoftness, 1.0 + uDissolveSoftness, vDissolveParams.x );
          float end_threshold   = mix( -uDissolveSoftness, 1.0 + uDissolveSoftness, vDissolveParams.y );
          float dissolve_t      = clamp( ( vProgress - vDissolveParams.z ) / max( 1.0 - vDissolveParams.z, 0.0001 ), 0.0, 1.0 );
          float dissolve_threshold = mix( start_threshold, end_threshold, dissolve_t );
          float dissolve_alpha = smoothstep( dissolve_threshold - uDissolveSoftness, dissolve_threshold + uDissolveSoftness, noise );

          // 2. ВОГНЯНИЙ ГРАДІЄНТ (як у SmokeBatch, але БЕЗ чорного)
          // Згасання полум'я від старту до uFireEndRatio
          float fire_decay = clamp( vProgress / max( uFireEndRatio, 0.001 ), 0.0, 1.0 );
          float fire_factor = clamp( noise * 1.25 - fire_decay * 1.5, 0.0, 1.0 );

          // Плавний перехід між двома кольорами (uColor1 0xFFDC50 -> uColor2 0xFF8C3C)
          vec3 fire_color = mix( uColor2, uColor1, smoothstep( 0.0, 0.5, fire_factor ) );
          vec3 final_color = fire_color;

          // М'який силуетний блік сфери (Fresnel: пряма проекція по осі Z)
          float fresnel = pow( 1.0 - abs( normalize( vViewNormal ).z ), 2.5 );
          final_color += fire_color * fresnel * 0.35;

          // Спалах по контуру напівпрозорого переходу тільки при наявності полум'я
          float rim_mask = smoothstep( 0.0, 0.5, dissolve_alpha ) * ( 1.0 - smoothstep( 0.5, 1.0, dissolve_alpha ) );
          final_color += uColor1 * ( rim_mask * 0.4 * fire_factor );

          // 3. ПЛАВНА ПРОЗОРІСТЬ (повне згасання до кінця анімації)
          float fade_out = 1.0 - smoothstep( 0.7, 1.0, vProgress );
          float alpha = dissolve_alpha * fade_out;

          gl_FragColor = vec4( final_color, alpha );
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
      side: THREE.FrontSide,
    } );

    super( { max_instances: VfxFireworkExplosionBlastBatch.MAX_INSTANCES, layout: FireworkExplosionBlastLayout }, base_geometry, material );
    this.instanced_mesh.renderOrder = VfxFireworkExplosionBlastBatch.RENDER_ORDER;
  }

  private static getSphereGeometry(): THREE.BufferGeometry
  {
    if ( VfxFireworkExplosionBlastBatch.cached_geometry )
      return VfxFireworkExplosionBlastBatch.cached_geometry;

    const geom = new THREE.IcosahedronGeometry( 1.0, 2 );
    VfxFireworkExplosionBlastBatch.cached_geometry = geom;
    return geom;
  }

  public spawnSphere(
    start_pos: THREE.Vector3,
    start_scale: number,
    end_scale: number,
    start_time: number,
    duration: number,
    dissolve_start_value: number,
    dissolve_end_value: number,
    dissolve_start_progress: number,
    rotation_axis?: THREE.Vector3,
    rotation_speed?: number,
    initial_angle?: number
  ): number
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( !mat.uniforms.uNoiseTexture.value )
    {
      try
      {
        mat.uniforms.uNoiseTexture.value = Vfx3DGlobalAtlas.getTexture();
        const uv = Vfx3DGlobalAtlas.getUVRect( ResourceID.VornoiNoiseTexture );
        mat.uniforms.uNoiseUv.value.set( uv.min_x, uv.min_y, uv.max_x, uv.max_y );
      }
      catch ( _e )
      {
        // Atlas not yet ready
      }
    }

    mat.uniforms.uColor1.value             = VfxFireworkExplosionBlastBatch.COLOR_1;
    mat.uniforms.uColor2.value             = VfxFireworkExplosionBlastBatch.COLOR_2;
    mat.uniforms.uDissolveSoftness.value   = VfxFireworkExplosionBlastBatch.DISSOLVE_SOFTNESS;
    mat.uniforms.uFireEndRatio.value       = VfxFireworkExplosionBlastBatch.FIRE_END_RATIO;

    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 )
      return -1;

    let rx = 0, ry = 1, rz = 0;
    if ( rotation_axis && rotation_axis.lengthSq() > 0.0001 )
    {
      const len = rotation_axis.length();
      rx = rotation_axis.x / len;
      ry = rotation_axis.y / len;
      rz = rotation_axis.z / len;
    }
    else
    {
      // Рівномірний випадковий вибір осі на 3D сфері
      const u = Math.random() * 2.0 - 1.0;
      const theta = Math.random() * Math.PI * 2.0;
      const r = Math.sqrt( Math.max( 0.0, 1.0 - u * u ) );
      rx = r * Math.cos( theta );
      ry = u;
      rz = r * Math.sin( theta );
    }

    const angle = initial_angle ?? Math.random() * Math.PI * 2;
    const speed = rotation_speed ?? ( ( Math.random() * 1.5 + 0.5 ) * ( Math.random() < 0.5 ? -1 : 1 ) );

    const pos_start_attr       = this.geometry.getAttribute( "aPosStartTime" ).array as Float32Array;
    const scale_rot_dur_attr   = this.geometry.getAttribute( "aScaleRotDur" ).array as Float32Array;
    const rot_axis_speed_attr  = this.geometry.getAttribute( "aRotationAxisSpeed" ).array as Float32Array;
    const dissolve_params_attr = this.geometry.getAttribute( "aDissolveParams" ).array as Float32Array;

    const o4 = idx * 4;
    const o3 = idx * 3;

    pos_start_attr[ o4 ]     = start_pos.x;
    pos_start_attr[ o4 + 1 ] = start_pos.y;
    pos_start_attr[ o4 + 2 ] = start_pos.z;
    pos_start_attr[ o4 + 3 ] = start_time;

    scale_rot_dur_attr[ o4 ]     = start_scale;
    scale_rot_dur_attr[ o4 + 1 ] = end_scale;
    scale_rot_dur_attr[ o4 + 2 ] = angle;
    scale_rot_dur_attr[ o4 + 3 ] = duration;

    rot_axis_speed_attr[ o4 ]     = rx;
    rot_axis_speed_attr[ o4 + 1 ] = ry;
    rot_axis_speed_attr[ o4 + 2 ] = rz;
    rot_axis_speed_attr[ o4 + 3 ] = speed;

    dissolve_params_attr[ o3 ]     = dissolve_start_value;
    dissolve_params_attr[ o3 + 1 ] = dissolve_end_value;
    dissolve_params_attr[ o3 + 2 ] = dissolve_start_progress;

    this.geometry.getAttribute( "aPosStartTime" ).needsUpdate       = true;
    this.geometry.getAttribute( "aScaleRotDur" ).needsUpdate        = true;
    this.geometry.getAttribute( "aRotationAxisSpeed" ).needsUpdate  = true;
    this.geometry.getAttribute( "aDissolveParams" ).needsUpdate     = true;

    return this.getStableId( idx );
  }

  protected override onUpdate( _dt: number, now: number ): void
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( mat.uniforms.uTime )
    {
      mat.uniforms.uTime.value = now;
    }
  }

  protected override updateInstance( _progress: number, _now: number, _idx: number ): boolean
  {
    return true;
  }
}
